import Papa from 'papaparse';

/**
 * Advanced CSV processor for Shakespeare plays
 * CSV format:
 * - gid: 4-digit unique integer identifier for each line
 * - act: Act number the line belongs to
 * - scene: Scene number the line belongs to
 * - is_stage_direction: STAGE_DIRECTION, CHARACTER, or LINE
 * - speaker: Character name speaking the line (LINE rows only)
 * - text: The dialogue or stage direction text
 * - line_in_scene: Line number within the scene (LINE type rows only)
 */

/**
 * Parse CSV file and extract comprehensive play statistics
 * @param {File} file - CSV file to parse
 * @returns {Promise<Object>} Parsed data with statistics
 */
export const parseShakespeareCSV = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header?.trim()?.toLowerCase(),
      complete: (results) => {
        try {
          const data = results.data;
          
          if (data?.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }

          const firstRow = data?.[0];
          const requiredColumns = ['gid', 'act', 'scene', 'line_in_scene', 'speaker', 'is_stage_direction', 'text'];
          const missingColumns = requiredColumns?.filter(col => !(col in firstRow));
          
          if (missingColumns?.length > 0) {
            reject(new Error(`CSV missing required columns: ${missingColumns?.join(', ')}`));
            return;
          }

          const statistics = analyzePlayData(data);
          
          resolve({
            ...statistics,
            rawData: data
          });
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(new Error(`CSV parsing error: ${error?.message}`));
      }
    });
  });
};

/**
 * Determine the line type from the is_stage_direction column value.
 * Supports both new string values (STAGE_DIRECTION, CHARACTER, LINE)
 * and legacy boolean values (true/false) for backward compatibility.
 * @param {string|boolean} value - Value from is_stage_direction column
 * @returns {string} One of: 'STAGE_DIRECTION', 'CHARACTER', 'LINE'
 */
const resolveLineType = (value) => {
  if (value === undefined || value === null) return 'LINE';
  
  const normalized = String(value)?.trim()?.toUpperCase();
  
  if (normalized === 'STAGE_DIRECTION') return 'STAGE_DIRECTION';
  if (normalized === 'CHARACTER') return 'CHARACTER';
  if (normalized === 'LINE') return 'LINE';
  
  // Legacy boolean support
  if (normalized === 'TRUE') return 'STAGE_DIRECTION';
  if (normalized === 'FALSE') return 'LINE';
  
  return 'LINE';
};

/**
 * Analyze play data to extract Acts, Scenes, Lines, and Characters
 * Uses gid column as unique identifier, act/scene columns for structure,
 * and line_in_scene for numbering LINE type rows within each scene.
 */
const analyzePlayData = (data) => {
  const acts = new Set();
  const scenes = new Map();
  const characters = new Set();
  const lines = [];

  // Track line_in_scene counter per scene for LINE type rows
  const sceneLineCounters = new Map();

  data?.forEach((row, index) => {
    // gid is the unique 4-digit identifier - skip rows without it
    const gidRaw = row?.gid?.toString()?.trim();
    if (!gidRaw || gidRaw === '') {
      return;
    }

    const gid = parseInt(gidRaw, 10);
    if (isNaN(gid)) {
      return;
    }

    // Read act and scene directly from their columns
    let actNumber = parseInt(row?.act, 10);
    let sceneNumber = parseInt(row?.scene, 10);

    // Fallback: if act/scene columns are missing, default to 1
    if (isNaN(actNumber) || actNumber <= 0) actNumber = 1;
    if (isNaN(sceneNumber) || sceneNumber <= 0) sceneNumber = 1;

    // Resolve line type
    const lineType = resolveLineType(row?.is_stage_direction);

    // Track acts
    acts?.add(actNumber);

    // Track scenes
    const sceneKey = `${actNumber}.${sceneNumber}`;
    if (!scenes?.has(sceneKey)) {
      scenes?.set(sceneKey, {
        act: actNumber,
        scene: sceneNumber,
        lineCount: 0,
        title: `Act ${toRoman(actNumber)}, Scene ${toRoman(sceneNumber)}`,
        description: row?.setting || row?.location || ''
      });
    }
    
    const scene = scenes?.get(sceneKey);
    if (scene) {
      scene.lineCount++;
    }

    // Speaker comes from the speaker column on LINE rows
    const speaker = row?.speaker?.trim() || null;

    // For CHARACTER rows: the text column contains the character name
    if (lineType === 'CHARACTER') {
      const characterName = row?.text?.trim();
      if (characterName) {
        characters?.add(characterName?.toUpperCase()?.trim());
      }
    }
    
    // For LINE rows: track unique speakers as characters too (fallback)
    if (lineType === 'LINE' && speaker) {
      characters?.add(speaker?.toUpperCase()?.trim());
    }

    // Determine line_in_scene:
    // Use the value from the CSV if present, otherwise auto-increment for LINE rows
    let lineInScene = null;
    if (lineType === 'LINE') {
      const csvLineInScene = parseInt(row?.line_in_scene, 10);
      if (!isNaN(csvLineInScene) && csvLineInScene > 0) {
        lineInScene = csvLineInScene;
      } else {
        // Auto-increment counter per scene for LINE rows
        const currentCount = sceneLineCounters?.get(sceneKey) || 0;
        lineInScene = currentCount + 1;
        sceneLineCounters?.set(sceneKey, lineInScene);
      }
    }

    lines?.push({
      gid: gid,
      // Keep lineNumber for backward compat (use gid value)
      lineNumber: gid,
      actNumber: actNumber,
      sceneNumber: sceneNumber,
      lineInScene: lineInScene,
      // speaker column identifies who speaks LINE rows
      character: lineType === 'LINE' ? speaker : null,
      dialogue: row?.text?.trim() || '',
      // Store the full line type string: STAGE_DIRECTION, CHARACTER, or LINE
      lineType: lineType,
      // Legacy boolean for backward compatibility
      isStageDirection: lineType === 'STAGE_DIRECTION',
      contextNotes: row?.context_notes || null,
      pronunciationHelp: row?.pronunciation_help || null
    });
  });

  const totalActs = acts?.size;
  const totalScenes = scenes?.size;
  const totalLines = lines?.length;
  const characterCount = characters?.size;

  return {
    totalActs: totalActs,
    totalScenes: totalScenes,
    totalLines: totalLines,
    characterCount: characterCount,
    acts: Array.from(acts)?.sort((a, b) => a - b),
    scenes: Array.from(scenes?.values())?.sort((a, b) => {
      if (a?.act !== b?.act) return a?.act - b?.act;
      return a?.scene - b?.scene;
    }),
    characters: Array.from(characters)?.sort(),
    lines: lines
  };
};

/**
 * Convert number to Roman numeral
 */
const toRoman = (num) => {
  if (num === 0) return '0';
  
  const romanMap = [
    { value: 10, numeral: 'X' },
    { value: 9, numeral: 'IX' },
    { value: 5, numeral: 'V' },
    { value: 4, numeral: 'IV' },
    { value: 1, numeral: 'I' }
  ];
  
  let result = '';
  let n = num;
  for (const { value, numeral } of romanMap) {
    while (n >= value) {
      result += numeral;
      n -= value;
    }
  }
  return result;
};

/**
 * Group lines by scenes for database insertion
 */
export const groupLinesByScene = (lines) => {
  const sceneLines = new Map();
  
  lines?.forEach(line => {
    const sceneKey = `${line?.actNumber}.${line?.sceneNumber}`;
    if (!sceneLines?.has(sceneKey)) {
      sceneLines?.set(sceneKey, []);
    }
    sceneLines?.get(sceneKey)?.push(line);
  });
  
  return sceneLines;
};

/**
 * Validate CSV analysis results
 */
export const validateAnalysis = (analysis) => {
  const warnings = [];
  
  const expectedActs = 5;
  const expectedScenes = 25;
  const expectedMinLines = 3800;
  const expectedMaxLines = 4000;
  
  if (analysis?.totalActs !== expectedActs) {
    warnings?.push(`Expected ${expectedActs} acts, found ${analysis?.totalActs}`);
  }
  
  if (analysis?.totalScenes !== expectedScenes) {
    warnings?.push(`Expected ${expectedScenes} scenes (including Prologue), found ${analysis?.totalScenes}`);
  }
  
  if (analysis?.totalLines < expectedMinLines || analysis?.totalLines > expectedMaxLines) {
    warnings?.push(`Expected ${expectedMinLines}-${expectedMaxLines} lines, found ${analysis?.totalLines}`);
  }
  
  if (analysis?.characterCount < 20) {
    warnings?.push(`Expected at least 20 unique characters, found ${analysis?.characterCount}`);
  }
  
  return {
    isValid: warnings?.length === 0,
    warnings: warnings,
    analysis: analysis
  };
};

/**
 * Validate character extraction from parsed CSV data.
 */
export const validateCharacterExtraction = (parsedData) => {
  const issues = [];
  const warnings = [];
  const characterDetails = [];

  const { lines = [], characters = [], rawData = [] } = parsedData || {};

  // --- Check 1: Are there any CHARACTER type rows? ---
  const characterTypeRows = lines?.filter(l => l?.lineType === 'CHARACTER');
  const hasCharacterRows = characterTypeRows?.length > 0;

  if (!hasCharacterRows) {
    issues?.push({
      type: 'MISSING_CHARACTER_ROWS',
      severity: 'error',
      message: 'No CHARACTER line type rows found in CSV. Characters cannot be properly identified without CHARACTER rows.',
      detail: 'Ensure the is_stage_direction column contains "CHARACTER" for rows that define character names.'
    });
  } else {
    const emptyCharacterRows = characterTypeRows?.filter(l => !l?.dialogue?.trim());
    if (emptyCharacterRows?.length > 0) {
      issues?.push({
        type: 'EMPTY_CHARACTER_ROWS',
        severity: 'error',
        message: `${emptyCharacterRows?.length} CHARACTER row(s) have empty text (character name missing).`,
        detail: `GIDs: ${emptyCharacterRows?.slice(0, 5)?.map(l => l?.gid)?.join(', ')}${emptyCharacterRows?.length > 5 ? '...' : ''}`
      });
    }
  }

  // --- Check 2: Build accurate line counts from LINE rows ---
  const lineCountMap = new Map();
  const speakerSet = new Set();

  lines?.forEach(line => {
    if (line?.lineType === 'LINE' && line?.character) {
      const key = line?.character?.toUpperCase()?.trim();
      speakerSet?.add(key);
      lineCountMap?.set(key, (lineCountMap?.get(key) || 0) + 1);
    }
  });

  // --- Check 3: Characters from CHARACTER rows vs speakers from LINE rows ---
  const extractedCharacters = new Set(
    characterTypeRows
      .map(l => l?.dialogue?.toUpperCase()?.trim())
      .filter(Boolean)
  );

  extractedCharacters?.forEach(charName => {
    const lineCount = lineCountMap?.get(charName) || 0;
    characterDetails?.push({
      name: charName,
      source: 'CHARACTER_ROW',
      lineCount,
      inExtractedList: characters?.includes(charName),
      hasSpeakingLines: lineCount > 0
    });

    if (lineCount === 0) {
      warnings?.push({
        type: 'CHARACTER_NO_LINES',
        severity: 'warning',
        message: `Character "${charName}" is defined in a CHARACTER row but has 0 speaking lines.`,
        detail: 'This may be intentional (non-speaking character) or a speaker column mismatch.'
      });
    }

    if (!characters?.includes(charName)) {
      issues?.push({
        type: 'CHARACTER_NOT_IN_LIST',
        severity: 'error',
        message: `Character "${charName}" was found in CHARACTER rows but is missing from the extracted characters list.`,
        detail: 'Check that the text column value on CHARACTER rows is not being filtered out.'
      });
    }
  });

  speakerSet?.forEach(speaker => {
    if (!extractedCharacters?.has(speaker)) {
      const lineCount = lineCountMap?.get(speaker) || 0;
      characterDetails?.push({
        name: speaker,
        source: 'LINE_SPEAKER',
        lineCount,
        inExtractedList: characters?.includes(speaker),
        hasSpeakingLines: true
      });
      warnings?.push({
        type: 'SPEAKER_WITHOUT_CHARACTER_ROW',
        severity: 'warning',
        message: `Speaker "${speaker}" appears in LINE rows but has no corresponding CHARACTER row.`,
        detail: `This character has ${lineCount} line(s). Add a CHARACTER row to properly define them.`
      });
    }
  });

  const allExpectedCharacters = new Set([
    ...Array.from(extractedCharacters),
    ...Array.from(speakerSet)
  ]);

  const missingFromExtracted = Array.from(allExpectedCharacters)?.filter(
    name => !characters?.includes(name)
  );

  if (missingFromExtracted?.length > 0) {
    issues?.push({
      type: 'CHARACTERS_MISSING_FROM_EXTRACTED_LIST',
      severity: 'error',
      message: `${missingFromExtracted?.length} character(s) are missing from the final extracted list.`,
      detail: `Missing: ${missingFromExtracted?.slice(0, 10)?.join(', ')}${missingFromExtracted?.length > 10 ? '...' : ''}`
    });
  }

  const totalSpeakingLines = Array.from(lineCountMap?.values())?.reduce((sum, c) => sum + c, 0);
  const totalLineRows = lines?.filter(l => l?.lineType === 'LINE')?.length;
  const unassignedLines = lines?.filter(l => l?.lineType === 'LINE' && !l?.character)?.length;

  if (unassignedLines > 0) {
    warnings?.push({
      type: 'UNASSIGNED_LINES',
      severity: 'warning',
      message: `${unassignedLines} LINE row(s) have no speaker assigned.`,
      detail: 'These lines will not be counted toward any character. Check the speaker column for these rows.'
    });
  }

  const isValid = issues?.length === 0;

  return {
    isValid,
    summary: {
      totalRawRows: rawData?.length,
      totalLineRows,
      totalCharacterRows: characterTypeRows?.length,
      totalStageDirectionRows: lines?.filter(l => l?.lineType === 'STAGE_DIRECTION')?.length,
      extractedCharacterCount: characters?.length,
      speakingCharacterCount: speakerSet?.size,
      totalSpeakingLines,
      unassignedLines
    },
    issues,
    warnings,
    characterDetails: characterDetails?.sort((a, b) => b?.lineCount - a?.lineCount)
  };
};

/**
 * Validate each CSV row and return per-row errors with line number, column, and reason.
 * @param {Array} rawData - Raw parsed CSV rows from Papa.parse
 * @returns {Array} Array of error objects: { csvLineNumber, column, value, reason }
 */
export const validateRowsDetailed = (rawData) => {
  const errors = [];

  if (!rawData || rawData?.length === 0) return errors;

  const VALID_LINE_TYPES = ['STAGE_DIRECTION', 'CHARACTER', 'LINE', 'TRUE', 'FALSE'];

  rawData?.forEach((row, index) => {
    // CSV line number: +2 because index is 0-based and row 1 is the header
    const csvLineNumber = index + 2;

    // --- gid validation ---
    const gidRaw = row?.gid?.toString()?.trim();
    if (!gidRaw || gidRaw === '') {
      errors?.push({
        csvLineNumber,
        column: 'gid',
        value: gidRaw ?? '',
        reason: 'Missing required field: gid cannot be empty'
      });
    } else {
      const gidNum = parseInt(gidRaw, 10);
      if (isNaN(gidNum)) {
        errors?.push({
          csvLineNumber,
          column: 'gid',
          value: gidRaw,
          reason: `Invalid value "${gidRaw}": gid must be a numeric integer`
        });
      }
    }

    // --- act validation ---
    const actRaw = row?.act?.toString()?.trim();
    if (!actRaw || actRaw === '') {
      errors?.push({
        csvLineNumber,
        column: 'act',
        value: actRaw ?? '',
        reason: 'Missing required field: act cannot be empty'
      });
    } else {
      const actNum = parseInt(actRaw, 10);
      if (isNaN(actNum) || actNum <= 0) {
        errors?.push({
          csvLineNumber,
          column: 'act',
          value: actRaw,
          reason: `Invalid value "${actRaw}": act must be a positive integer`
        });
      }
    }

    // --- scene validation ---
    const sceneRaw = row?.scene?.toString()?.trim();
    if (!sceneRaw || sceneRaw === '') {
      errors?.push({
        csvLineNumber,
        column: 'scene',
        value: sceneRaw ?? '',
        reason: 'Missing required field: scene cannot be empty'
      });
    } else {
      const sceneNum = parseInt(sceneRaw, 10);
      if (isNaN(sceneNum) || sceneNum <= 0) {
        errors?.push({
          csvLineNumber,
          column: 'scene',
          value: sceneRaw,
          reason: `Invalid value "${sceneRaw}": scene must be a positive integer`
        });
      }
    }

    // --- is_stage_direction validation ---
    const lineTypeRaw = row?.is_stage_direction?.toString()?.trim()?.toUpperCase();
    if (!lineTypeRaw || lineTypeRaw === '') {
      errors?.push({
        csvLineNumber,
        column: 'is_stage_direction',
        value: lineTypeRaw ?? '',
        reason: 'Missing required field: is_stage_direction cannot be empty'
      });
    } else if (!VALID_LINE_TYPES?.includes(lineTypeRaw)) {
      errors?.push({
        csvLineNumber,
        column: 'is_stage_direction',
        value: row?.is_stage_direction,
        reason: `Invalid value "${row?.is_stage_direction}": must be STAGE_DIRECTION, CHARACTER, or LINE`
      });
    }

    // --- text validation ---
    const textRaw = row?.text?.toString()?.trim();
    if (!textRaw || textRaw === '') {
      errors?.push({
        csvLineNumber,
        column: 'text',
        value: '',
        reason: 'Missing required field: text cannot be empty'
      });
    }

    // --- speaker validation for LINE rows ---
    const resolvedType = lineTypeRaw;
    if (resolvedType === 'LINE') {
      const speakerRaw = row?.speaker?.toString()?.trim();
      if (!speakerRaw || speakerRaw === '') {
        errors?.push({
          csvLineNumber,
          column: 'speaker',
          value: '',
          reason: 'Missing speaker: LINE rows must have a speaker value'
        });
      }
    }

    // --- line_in_scene validation for LINE rows ---
    if (resolvedType === 'LINE') {
      const lisRaw = row?.line_in_scene?.toString()?.trim();
      if (lisRaw && lisRaw !== '') {
        const lisNum = parseInt(lisRaw, 10);
        if (isNaN(lisNum) || lisNum <= 0) {
          errors?.push({
            csvLineNumber,
            column: 'line_in_scene',
            value: lisRaw,
            reason: `Invalid value "${lisRaw}": line_in_scene must be a positive integer when provided`
          });
        }
      }
    }
  });

  return errors;
};